import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { OccEndpointsService, ProductSearchPage, WindowRef } from '@spartacus/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { DpRelatedPartProductNormalizer } from '@app/core/converters/related-part-product.normalizer';
import { DpSearchService } from '@app/core/services/search.service';
import { DpProduct } from '@app/core/types/view/dp-product.interface';

import { DpQuickOrderEntry } from '../types';

@Injectable({ providedIn: 'root' })
export class DpQuickOrderService {
  entriesSubject$: BehaviorSubject<DpQuickOrderEntry[]> = new BehaviorSubject([] as DpQuickOrderEntry[]);
  entries$ = this.entriesSubject$.asObservable();
  resultsOpenSubject = new BehaviorSubject(false);
  resultsOpen$ = this.resultsOpenSubject.asObservable();

  readonly #winRef = inject(WindowRef);
  readonly #relatedPartsNormalizer = inject(DpRelatedPartProductNormalizer);

  constructor(
    private readonly http: HttpClient,
    private readonly occEndpoints: OccEndpointsService,
    private readonly dpSearchService: DpSearchService
  ) {
    const sessionSavedEntries = this.#winRef.sessionStorage?.getItem('dpQuickOrderEntries');

    if (sessionSavedEntries) {
      this.entriesSubject$.next(JSON.parse(sessionSavedEntries));
    }
  }

  closeResults(): void {
    this.resultsOpenSubject.next(false);
  }

  openResults(): void {
    this.resultsOpenSubject.next(true);
  }

  saveEntry(entry: DpQuickOrderEntry): void {
    this.entriesSubject$.pipe(take(1)).subscribe((savedEntries) => {
      const entryFound = savedEntries.find(({ product }) => product.code === entry.product.code);
      const entriesWithoutExisting = savedEntries.filter(({ product }) => product.code !== entry.product.code);

      this.updateEntries([
        ...entriesWithoutExisting,
        { ...entry, index: entryFound?.index ?? entriesWithoutExisting.length }
      ]);
    });
  }

  deleteEntries(): void {
    this.entriesSubject$.next([]);
    this.#winRef.sessionStorage?.removeItem('dpQuickOrderEntries');
  }

  deleteEntry(productCode: string): void {
    this.entriesSubject$.pipe(take(1)).subscribe((entries) => {
      const filtered = entries.filter(({ product }) => product.code !== productCode);

      this.updateEntries(filtered);
    });
  }

  updateEntries(newEntries: DpQuickOrderEntry[]): void {
    this.entriesSubject$.next(newEntries.sort(({ index = 0 }, { index: indexb = 0 }) => indexb - index));
    this.#winRef.sessionStorage?.setItem('dpQuickOrderEntries', JSON.stringify([...newEntries]));
  }

  getProducts(query: string): Observable<DpProduct[] | undefined> {
    query = encodeURI(this.dpSearchService.escapeTerm(query));
    const url = this.occEndpoints.buildUrl(
      `products/search?fields=relatedParts(FULL)&pagination(DEFAULT)&sorts(DEFAULT)&query=${query}&pageSize=10`
    );

    return (this.http.get(url) as Observable<ProductSearchPage>).pipe(
      map(({ relatedParts }) => this.#relatedPartsNormalizer.convert(relatedParts))
    );
  }
}
